import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellRing, Clock, LogOut, Receipt, Plus, Trash2, X, Search, ChefHat, Info, ShieldAlert, AlertTriangle, Sparkles, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

const NEXT = { placed: "preparing", preparing: "ready", ready: "served", served: "paid" };
const STALE_MS = 10 * 60 * 1000; // 10 minutes

function fmtElapsed(ms) {
  if (ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec.toString().padStart(2, "0")}s`;
}

function fmtTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

function lower(arr) { return (arr || []).map((x) => String(x).toLowerCase()); }

export default function StaffDashboard() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [tab, setTab] = useState("kds");
  const [now, setNow] = useState(Date.now());
  const [addPickerFor, setAddPickerFor] = useState(null); // order id being modified
  const [briefingId, setBriefingId] = useState(null); // dish id for plating/definition modal

  const load = async () => {
    try {
      // today_only defaults to true server-side, but we pass it explicitly for clarity.
      const [o, m] = await Promise.all([
        api.get("/staff/orders", { params: { today_only: true } }),
        api.get("/staff/menu"),
      ]);
      setOrders(o.data); setMenu(m.data);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); const int = setInterval(load, 10000); return () => clearInterval(int); }, []);
  // Tick every second so elapsed timers stay live without re-fetching.
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const advance = async (o) => {
    const ns = NEXT[o.status] || "paid";
    try {
      await api.put(`/staff/orders/${o.id}/status`, { status: ns });
      toast.success(`Marked ${ns}`);
      load();
    } catch { toast.error("Update failed"); }
  };

  const markPaid = async (o) => {
    try {
      await api.post(`/staff/orders/${o.id}/mark-paid`, { method: "cash" });
      toast.success(`Payment received for Table ${o.table_no}`);
      load();
    } catch { toast.error("Could not mark paid"); }
  };

  const toggleSold = async (item) => {
    try {
      await api.put(`/staff/menu/${item.id}/sold-out`, { sold_out: !item.is_sold_out });
      toast.success(item.is_sold_out ? "Back in stock" : "Marked sold out");
      load();
    } catch { toast.error("Could not update"); }
  };

  const deleteLine = async (o, idx, name) => {
    if (!window.confirm(`Remove "${name}" from Table ${o.table_no}?`)) return;
    try {
      await api.delete(`/staff/orders/${o.id}/items/${idx}`);
      toast.success(`Removed ${name}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const addDish = async (orderId, item) => {
    try {
      await api.post(`/staff/orders/${orderId}/append`, { items: [{ item_id: item.id, qty: 1 }] });
      toast.success(`Added ${item.name}`);
      setAddPickerFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
  };

  const paidAlerts = orders.filter((o) => o.paid && o.status !== "served" && o.status !== "closed");
  const waiterCalls = orders.filter((o) => o.waiter_called);
  const activeOrder = useMemo(() => orders.find((o) => o.id === addPickerFor) || null, [orders, addPickerFor]);

  const ackCall = async (o) => {
    try {
      await api.post(`/staff/orders/${o.id}/acknowledge-call`);
      toast.success(`Heading to Table ${o.table_no}`);
      load();
    } catch { toast.error("Could not acknowledge"); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 py-4">
          <div className="flex items-center gap-3">
            <Logo size={36} />
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Staff console · Today</div>
              <div className="font-display text-xl">{user?.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {waiterCalls.length > 0 && (
              <motion.div animate={{ scale: [1, 1.04, 1] }} transition={{ repeat: Infinity, duration: 1.2 }} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-300 text-xs" data-testid="waiter-calls">
                <BellRing className="w-3.5 h-3.5" /> {waiterCalls.length} tables calling
              </motion.div>
            )}
            {paidAlerts.length > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs" data-testid="paid-alerts">
                <Bell className="w-3.5 h-3.5" /> {paidAlerts.length} tables paid
              </div>
            )}
            <button onClick={logout} className="text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5" data-testid="staff-logout"><LogOut className="w-3.5 h-3.5 inline mr-1" /> Sign out</button>
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-5 pb-3 flex gap-2">
          <Tab active={tab === "kds"} onClick={() => setTab("kds")} label="KDS — Today" count={orders.filter((o) => !["paid", "served", "closed"].includes(o.status)).length} testid="tab-kds" />
          <Tab active={tab === "menu"} onClick={() => setTab("menu")} label="Inventory" testid="tab-inventory" />
          <Tab active={tab === "assistant"} onClick={() => setTab("assistant")} label="Assistant" testid="tab-assistant" />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-5 py-6">
        <AnimatePresence>
          {waiterCalls.length > 0 && tab === "kds" && (
            <motion.section initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mb-5 p-4 rounded-2xl border border-orange-500/40 bg-orange-500/10" data-testid="waiter-call-banner">
              <div className="flex items-center gap-2 text-orange-300">
                <BellRing className="w-4 h-4" />
                <div className="text-[11px] font-mono uppercase tracking-[0.2em]">Bill requested — go now</div>
              </div>
              <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {waiterCalls.map((o) => (
                  <div key={o.id} className="p-3 rounded-xl bg-[#0F1115] border border-orange-500/30 flex items-center justify-between" data-testid={`waiter-call-${o.id}`}>
                    <div>
                      <div className="font-display text-lg">Table {o.table_no}</div>
                      <div className="text-xs text-white/50 font-mono">{o.customer_name || "Guest"} · ₹{o.total?.toFixed?.(0)}</div>
                    </div>
                    <button onClick={() => ackCall(o)} data-testid={`ack-call-${o.id}`} className="gold-btn rounded-full px-3 py-1.5 text-xs font-semibold">On it</button>
                  </div>
                ))}
              </div>
            </motion.section>
          )}
        </AnimatePresence>

        {tab === "kds" && (
          <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="kds-grid">
            {orders.length === 0 && <div className="text-white/40 col-span-full text-center py-12">Kitchen's quiet. Your next ticket will land here.</div>}
            {orders.map((o) => {
              const createdMs = o.created_at ? new Date(o.created_at).getTime() : now;
              const elapsed = now - createdMs;
              const stale = elapsed > STALE_MS;
              const isPaid = o.paid || o.status === "paid" || o.status === "closed";
              const guestFlags = o.allergen_flags || [];
              const guestFlagsLower = lower(guestFlags);
              const hasConflict = (o.items || []).some((it) => lower(it.dish_allergens).some((a) => guestFlagsLower.includes(a)));
              return (
                <motion.div layout key={o.id} className={`p-4 rounded-2xl border bg-[#121418] ${hasConflict ? "border-red-500/60" : "border-white/10"}`} data-testid={`kds-ticket-${o.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Table {o.table_no} · #{o.id.slice(0, 6)}</div>
                      <div className="font-display text-lg">{o.customer_name || "Guest"}</div>
                    </div>
                    <StatusPill status={o.status} />
                  </div>

                  {guestFlags.length > 0 && (
                    <motion.div
                      animate={hasConflict ? { boxShadow: ["0 0 0 0 rgba(239,68,68,0.6)", "0 0 0 6px rgba(239,68,68,0)", "0 0 0 0 rgba(239,68,68,0)"] } : undefined}
                      transition={hasConflict ? { repeat: Infinity, duration: 1.6 } : undefined}
                      className={`mt-2 p-2 rounded-lg border text-xs flex items-start gap-2 ${hasConflict ? "bg-red-500/15 border-red-500/50 text-red-200" : "bg-amber-500/10 border-amber-500/40 text-amber-200"}`}
                      data-testid={`guest-allergy-${o.id}`}
                    >
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <div>
                        <div className="font-mono uppercase tracking-widest text-[9px] opacity-90">Guest allergy</div>
                        <div>{guestFlags.join(", ")}{hasConflict ? " · CONFLICT — confirm with guest" : ""}</div>
                      </div>
                    </motion.div>
                  )}

                  <div className="mt-3 space-y-1.5 text-sm" data-testid={`kds-items-${o.id}`}>
                    {o.items.map((it, idx) => {
                      const dishAllergens = it.dish_allergens || [];
                      const conflictHere = dishAllergens.length > 0 && lower(dishAllergens).some((a) => guestFlagsLower.includes(a));
                      return (
                        <div key={`${o.id}-${idx}`} data-testid={`kds-line-${o.id}-${idx}`} className="group">
                          <div className="flex justify-between items-center gap-2">
                            <span className="flex-1">{it.qty}× {it.name}{it.notes ? ` — ${it.notes}` : ""}</span>
                            <span className="font-mono text-white/60">₹{(it.price * it.qty).toFixed(0)}</span>
                            <button
                              onClick={() => setBriefingId(it.item_id)}
                              data-testid={`plate-guide-${o.id}-${idx}`}
                              title="Plate guide & definition"
                              className="w-6 h-6 rounded-full flex items-center justify-center text-[#E5A93C] hover:bg-[#E5A93C]/10 opacity-70 hover:opacity-100"
                            >
                              <ChefHat className="w-3 h-3" />
                            </button>
                            {!isPaid && (
                              <button
                                onClick={() => deleteLine(o, idx, it.name)}
                                data-testid={`delete-line-${o.id}-${idx}`}
                                title={`Remove ${it.name}`}
                                className="w-6 h-6 rounded-full flex items-center justify-center text-red-400 hover:bg-red-500/15 opacity-60 hover:opacity-100"
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                          {dishAllergens.length > 0 && (
                            <div
                              data-testid={`dish-allergens-${o.id}-${idx}`}
                              className={`mt-0.5 ml-0 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded ${conflictHere ? "bg-red-500/20 text-red-300 border border-red-500/40" : "bg-white/5 text-white/50 border border-white/10"}`}
                            >
                              <ShieldAlert className="w-2.5 h-2.5" />
                              contains: {dishAllergens.join(", ")}
                              {conflictHere && <span className="ml-1 font-bold">⚠ guest-flagged</span>}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {!isPaid && (
                    <button
                      onClick={() => setAddPickerFor(o.id)}
                      data-testid={`open-add-dish-${o.id}`}
                      className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/10 hover:bg-white/5 text-white/70"
                    >
                      <Plus className="w-3 h-3" /> Add dish
                    </button>
                  )}

                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-white/50 flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Placed {fmtTime(o.created_at)}
                      </span>
                      {!isPaid && (
                        <span
                          data-testid={`kds-timer-${o.id}`}
                          className={`text-[11px] font-mono font-semibold ${stale ? "text-red-400" : "text-white/70"}`}
                        >
                          {fmtElapsed(elapsed)} elapsed{stale ? " · overdue" : ""}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!o.paid && (
                        <button onClick={() => markPaid(o)} data-testid={`mark-paid-${o.id}`} className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-semibold">
                          <Receipt className="w-3 h-3" /> Mark paid
                        </button>
                      )}
                      {o.status !== "paid" && o.status !== "served" && (
                        <button onClick={() => advance(o)} data-testid={`advance-${o.id}`} className="gold-btn rounded-full px-4 py-1.5 text-xs font-semibold">
                          Mark {NEXT[o.status] || "done"}
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </section>
        )}

        {tab === "menu" && (
          <section className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="inventory-grid">
            {menu.map((m) => (
              <div key={m.id} className={`p-3 rounded-2xl border bg-[#121418] flex items-center gap-3 ${m.is_sold_out ? "border-red-500/40" : "border-white/10"}`} data-testid={`inv-${m.id}`}>
                <img src={m.image_url} alt={m.name} className="w-14 h-14 rounded-xl object-cover" />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-base truncate">{m.name}</div>
                  <div className="text-xs text-white/50 font-mono">₹{m.price} · {m.times_ordered_today} today</div>
                </div>
                <button onClick={() => toggleSold(m)} data-testid={`toggle-sold-${m.id}`} className={`text-xs px-3 py-1.5 rounded-full font-semibold ${m.is_sold_out ? "bg-red-500/20 text-red-300 border border-red-500/40" : "gold-btn"}`}>
                  {m.is_sold_out ? "Sold out" : "Live"}
                </button>
              </div>
            ))}
          </section>
        )}

        {tab === "assistant" && (
          <AssistantTab menu={menu} onPick={(id) => setBriefingId(id)} />
        )}
      </main>

      {activeOrder && (
        <AddDishModal
          order={activeOrder}
          menu={menu}
          onClose={() => setAddPickerFor(null)}
          onPick={(item) => addDish(activeOrder.id, item)}
        />
      )}

      {briefingId && (
        <BriefingModal dishId={briefingId} onClose={() => setBriefingId(null)} />
      )}
    </div>
  );
}

function AssistantTab({ menu, onPick }) {
  const [q, setQ] = useState("");
  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return menu;
    return menu.filter((m) => m.name.toLowerCase().includes(term)
      || (m.description || "").toLowerCase().includes(term)
      || (m.ingredients || []).some((x) => String(x).toLowerCase().includes(term))
    );
  }, [menu, q]);

  return (
    <section data-testid="assistant-tab">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-[#E5A93C]" />
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Waitstaff Digital Assistant</div>
          <div className="font-display text-lg">Tap any dish for the plating guide & culinary brief</div>
        </div>
      </div>
      <div className="relative mb-4 max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by dish, ingredient, region…"
          data-testid="assistant-search"
          className="w-full bg-[#121418] border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#E5A93C]"
        />
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="assistant-grid">
        {list.length === 0 && <div className="text-white/40 col-span-full text-center py-12">No dishes match your search.</div>}
        {list.map((m) => (
          <button
            key={m.id}
            onClick={() => onPick(m.id)}
            data-testid={`assistant-card-${m.id}`}
            className="text-left p-3 rounded-2xl border border-white/10 bg-[#121418] hover:border-[#E5A93C]/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              {m.image_url ? <img src={m.image_url} alt="" className="w-14 h-14 rounded-xl object-cover" /> : <div className="w-14 h-14 rounded-xl bg-white/5" />}
              <div className="flex-1 min-w-0">
                <div className="font-display text-base truncate">{m.name}</div>
                <div className="text-[11px] text-white/50 font-mono truncate">{(m.ingredients || []).slice(0, 3).join(" · ") || "—"}</div>
                <div className="mt-1 flex items-center gap-1 flex-wrap">
                  {(m.allergens || []).slice(0, 4).map((a) => (
                    <span key={a} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/5 text-white/60 border border-white/10">{a}</span>
                  ))}
                  {m.plating_video_url && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#E5A93C]/15 text-[#E5A93C] border border-[#E5A93C]/30 inline-flex items-center gap-1"><PlayCircle className="w-2.5 h-2.5" /> video</span>}
                </div>
              </div>
              <Info className="w-4 h-4 text-[#E5A93C] shrink-0" />
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

function BriefingModal({ dishId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/staff/menu/${dishId}/assistant`);
        if (alive) setData(d);
      } catch { if (alive) setData(null); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [dishId]);

  const isYouTube = (url = "") => /youtube\.com|youtu\.be/.test(url);
  const ytEmbed = (url = "") => {
    try {
      if (url.includes("youtu.be/")) return url.replace("youtu.be/", "www.youtube.com/embed/").split("?")[0];
      if (url.includes("watch?v=")) {
        const id = new URL(url).searchParams.get("v");
        return `https://www.youtube.com/embed/${id}`;
      }
      if (url.includes("/embed/")) return url;
    } catch { /* noop */ }
    return url;
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="briefing-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl rounded-3xl p-5 max-h-[90vh] overflow-y-auto bg-[#121418] border border-white/10 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Plating brief</div>
            <div className="font-display text-2xl">{loading ? "Loading…" : (data?.name || "Dish")}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10" data-testid="close-briefing"><X className="w-4 h-4" /></button>
        </div>

        {!loading && data && (
          <div className="mt-4 space-y-4">
            {data.image_url && (
              <img src={data.image_url} alt="" className="w-full h-44 object-cover rounded-2xl" />
            )}

            {data.plating_video_url ? (
              <div data-testid="briefing-video">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-1.5"><PlayCircle className="w-3 h-3" /> Plating guide</div>
                {isYouTube(data.plating_video_url) ? (
                  <iframe
                    src={ytEmbed(data.plating_video_url)}
                    title="plating"
                    className="w-full aspect-video rounded-2xl border border-white/10"
                    allow="autoplay; encrypted-media; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video controls src={data.plating_video_url} className="w-full rounded-2xl border border-white/10 bg-black" />
                )}
              </div>
            ) : (
              <div className="text-xs text-white/40 italic" data-testid="briefing-no-video">No plating guide uploaded yet — ask the chef on shift.</div>
            )}

            {data.culinary_definition ? (
              <div data-testid="briefing-definition">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">Culinary definition</div>
                <p className="text-sm leading-relaxed text-white/85">{data.culinary_definition}</p>
              </div>
            ) : data.description ? (
              <div data-testid="briefing-description">
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">Description</div>
                <p className="text-sm leading-relaxed text-white/70">{data.description}</p>
              </div>
            ) : null}

            {(data.ingredients || []).length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5">Ingredients</div>
                <div className="flex flex-wrap gap-1.5" data-testid="briefing-ingredients">
                  {data.ingredients.map((ing) => (
                    <span key={ing} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10">{ing}</span>
                  ))}
                </div>
              </div>
            )}

            {(data.allergens || []).length > 0 && (
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-white/50 mb-1.5 flex items-center gap-1.5"><ShieldAlert className="w-3 h-3" /> Allergens</div>
                <div className="flex flex-wrap gap-1.5" data-testid="briefing-allergens">
                  {data.allergens.map((a) => (
                    <span key={a} className="text-xs px-2.5 py-1 rounded-full bg-red-500/15 text-red-300 border border-red-500/30">{a}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AddDishModal({ order, menu, onClose, onPick }) {
  const [q, setQ] = useState("");
  const items = useMemo(() => {
    const term = q.trim().toLowerCase();
    const base = menu.filter((m) => !m.is_sold_out);
    if (!term) return base.slice(0, 50);
    return base.filter((m) => m.name.toLowerCase().includes(term)).slice(0, 50);
  }, [menu, q]);

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="add-dish-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-3xl p-5 max-h-[85vh] overflow-hidden flex flex-col bg-[#121418] border border-white/10 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/50">Add to Table {order.table_no}</div>
            <div className="font-display text-lg">{order.customer_name || "Guest"}</div>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center bg-white/5 hover:bg-white/10" data-testid="close-add-dish"><X className="w-4 h-4" /></button>
        </div>
        <div className="mt-4 relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search dishes…"
            autoFocus
            data-testid="add-dish-search"
            className="w-full bg-[#0A0A0C] border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#E5A93C]"
          />
        </div>
        <div className="mt-3 flex-1 overflow-y-auto divide-y divide-white/5" data-testid="add-dish-list">
          {items.length === 0 && <div className="text-center text-white/40 py-10 text-sm">No matching dishes</div>}
          {items.map((m) => (
            <button
              key={m.id}
              onClick={() => onPick(m)}
              data-testid={`add-dish-option-${m.id}`}
              className="w-full flex items-center gap-3 py-2.5 px-1 text-left hover:bg-white/5 rounded-lg"
            >
              {m.image_url ? (
                <img src={m.image_url} alt="" className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 rounded-lg bg-white/5" />
              )}
              <div className="flex-1 min-w-0">
                <div className="text-sm truncate">{m.name}</div>
                <div className="text-[11px] text-white/50 font-mono">₹{m.price}</div>
              </div>
              <Plus className="w-4 h-4 text-[#E5A93C]" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Tab({ active, onClick, label, count, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`px-4 py-2 rounded-full text-sm transition-colors border flex items-center gap-2 ${active ? "bg-[#E5A93C] text-black border-[#E5A93C] font-semibold" : "border-white/10 hover:bg-white/5 text-white/70"}`}>
      {label} {count != null && <span className={`text-[10px] px-1.5 rounded-full ${active ? "bg-black/15" : "bg-white/10"}`}>{count}</span>}
    </button>
  );
}

function StatusPill({ status }) {
  const map = {
    placed: "bg-amber-500/15 text-amber-300",
    preparing: "bg-blue-500/15 text-blue-300",
    ready: "bg-emerald-500/15 text-emerald-300",
    served: "bg-white/10 text-white/60",
    paid: "bg-[#E5A93C]/15 text-[#E5A93C]",
  };
  return <span className={`text-[10px] px-2 py-1 rounded-full font-mono uppercase tracking-widest ${map[status] || "bg-white/10"}`}>{status}</span>;
}
